import { Link, MemoryRouter, useInRouterContext, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, PieChart, FileText, Images, ShoppingCart, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { fetchShoppingList } from "@/lib/shopping-list-api";
import {
  isCrmActive,
  isDashboardActive,
  isImagesActive,
  isManualsActive,
  isContactsActive,
  isShoppingListActive,
} from "@/lib/nav-active";

interface NavItem {
  icon: React.ReactNode;
  label: string;
  to: string;
  check?: (pathname: string) => boolean;
}

function BottomNavigationInner() {
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const { data: shoppingData } = useQuery({
    queryKey: queryKeys.shoppingList,
    queryFn: fetchShoppingList,
    staleTime: 60 * 1000,
    retry: false,
  });
  const shoppingPendingTotal = shoppingData?.pending_total ?? 0;

  const navItems: NavItem[] = [
    {
      icon: <PieChart className="w-5 h-5" />,
      label: t("sidebar.dashboard"),
      to: "/dashboard",
      check: isDashboardActive,
    },
    {
      icon: <LayoutDashboard className="w-5 h-5" />,
      label: t("sidebar.crm"),
      to: "/crm",
      check: isCrmActive,
    },
    {
      icon: <FileText className="w-5 h-5" />,
      label: t("sidebar.manuals"),
      to: "/manuals",
      check: isManualsActive,
    },
    {
      icon: <Images className="w-5 h-5" />,
      label: t("sidebar.images"),
      to: "/images/gallery",
      check: isImagesActive,
    },
    {
      icon: <ShoppingCart className="w-5 h-5" />,
      label: t("sidebar.shoppingList"),
      to: "/shopping-list",
      check: isShoppingListActive,
    },
    {
      icon: <Users className="w-5 h-5" />,
      label: t("sidebar.contacts"),
      to: "/contacts",
      check: isContactsActive,
    },
  ];

  const isActive = (item: NavItem) => {
    if (item.check) return item.check(pathname);
    return pathname === item.to || pathname.startsWith(item.to + "/");
  };

  // Only show on mobile
  if (!isMobile) {
    return null;
  }

  // Check if running in standalone PWA mode
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border"
      style={{
        paddingBottom: isStandalone
          ? 'max(0px, calc(env(safe-area-inset-bottom) - 10px))'
          : '0',
        bottom: '0',
      }}
    >
      <div className="flex items-center justify-around h-16 px-1 overflow-x-auto">
        {navItems.map((item) => {
          const active = isActive(item);
          const isShopping = item.to === "/shopping-list";
          const showBadge = isShopping && shoppingPendingTotal > 0;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex flex-col items-center justify-center flex-shrink-0 min-w-[60px] max-w-[80px] px-1 py-1 rounded-lg transition-colors relative",
                active
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <span className="relative inline-flex">
                {item.icon}
                {showBadge && (
                  <span className="absolute -top-1 -right-2 min-w-[1rem] h-4 px-1 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold">
                    {shoppingPendingTotal > 99 ? "99+" : shoppingPendingTotal}
                  </span>
                )}
              </span>
              <span className="text-[10px] mt-0.5 font-medium truncate w-full text-center leading-tight">
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default function BottomNavigation() {
  const inRouter = useInRouterContext();

  if (!inRouter) {
    return (
      <MemoryRouter>
        <BottomNavigationInner />
      </MemoryRouter>
    );
  }

  return <BottomNavigationInner />;
}
